<template>
  <v-container fluid>
    <v-snackbar
        id="confirmRegisterDialog"
        v-model="showConfirmation"
        :timeout="6000"
        top
        :dark="$vuetify.theme.dark"
        multi-line>
      {{ $t('Подтвердите регистрацию перейдя по ссылке полученной из почтового сообщения, отправленного на адрес') }} {{ newUser.email }}.
      <v-btn
          text
          color="red"
          @click="showConfirmation = false">
        {{ $t('Закрыть') }}
      </v-btn>
    </v-snackbar>
    <v-row justify="center" class="text-center">
      <v-col
          cols="12"
          sm="8"
          md="7"
          lg="6"
          xl="6"
          class="text-center">
        <v-card class="elevation-12" max-width="600px">
          <v-toolbar :dark="$vuetify.theme.dark" color="primary">
            <v-toolbar-title>
              {{ $t('Создание учетной записи') }}
            </v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <v-progress-linear :active="isLoading" color="loader" indeterminate />
            <general-error v-if="!!generalError" />
            <!-- focusOnBtn метод из глобального миксина -->
            <ValidationObserver ref="registrationform" @keydown.native.enter="focusOnBtn('registerBtn')">
              <form>
                <validatable-text-input
                    v-model="newUser.login"
                    v-test="{ input: 'login' }"
                    :rules="{ required: true,
                              min: 3,
                              max: 25,
                              regex: /^([a-zA-Z0-9-._@+]+)$/ }"
                    :label="$t('Логин')"
                    prepend-icon="person" />
                <validatable-text-input
                    v-model="newUser.password"
                    v-test="{ input: 'password' }"
                    vid="password"
                    rules="required|max:100"
                    :label="$t('Пароль')"
                    prepend-icon="lock"
                    type="password" />
                <validatable-text-input
                    v-model="newUser.passwordConfirmation"
                    v-test="{ input: 'passwordConfirmation' }"
                    rules="required|confirmed:password"
                    :label="$t('Подтверждение пароля')"
                    prepend-icon="lock"
                    type="password" />
                <validatable-text-input
                    v-model="newUser.email"
                    v-test="{ input: 'email' }"
                    rules="required|email"
                    label="Email"
                    prepend-icon="alternate_email"
                    type="email" />
                <v-row class="my-2">
                  <v-col cols="12" class="text-left">
                    <!-- focusOnBtn метод из глобального миксина -->
                    <v-checkbox
                        v-model="approved"
                        v-test="{ checkbox: 'approved' }"
                        readonly
                        @change="focusOnBtn('registerBtn')"
                        @click="policyDialog.show = true">
                      <template v-slot:label>
                        {{ flag }}
                      </template>
                    </v-checkbox>
                    <privacy-policy-dialog
                        v-if="policyDialog.show"
                        v-model="approved"
                        :show="policyDialog.show"
                        @close="policyDialog.show = false" />
                  </v-col>
                </v-row>
                <validatable-recaptcha
                    v-if="captchaMethod === CAPTCHA_METHODS.GOOGLE"
                    ref="recaptcha"
                    v-model="newUser.captcha" />
                <validatable-captcha-gen
                    v-if="captchaMethod === CAPTCHA_METHODS.CAPTCHA_GEN"
                    ref="captchagen"
                    :captcha="newUser.captcha"
                    @input="inputCaptchaGen" />
              </form>
            </ValidationObserver>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
                id="cancelBtn"
                v-test="{ btn: 'cancel' }"
                color="primary"
                @click.stop="cancel">
              {{ $t('Отмена') }}
            </v-btn>
            <v-btn
                id="registerBtn"
                ref="registerBtn"
                v-test="{ btn: 'register' }"
                color="primary"
                :disabled="isLoading || !approved"
                @click.stop="validateAndRegister">
              {{ $t('Регистрация') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped>
::v-deep .caption {
  font-size: 10pc;
}
</style>

<script>
  import GeneralError from '@/components/reused/common/GeneralError.vue'
  import PrivacyPolicyDialog from '@/components/reused/common/PrivacyPolicyDialog.vue'
  import ValidatableTextInput from '@/components/reused/ui/ValidatableTextInput.vue'
  import ValidatableRecaptcha from '@/components/reused/ui/ValidatableRecaptcha.vue'
  import ValidatableCaptchaGen from '@/components/reused/ui/ValidatableCaptchaGen.vue'
  import { CAPTCHA_METHODS } from '@/constants/business'
  import { mapActions, mapState } from 'vuex'
  import { ACCOUNT, CAPTCHA } from '@/constants/store.js'

  export default {
    components: {
      ValidatableTextInput,
      ValidatableRecaptcha,
      ValidatableCaptchaGen,
      GeneralError,
      PrivacyPolicyDialog
    },
    data () {
      return {
        showConfirmation: false,
        isLoading: false,
        approved: null,
        policyDialog: {
          show: false
        },
        newUser: {
          login: '',
          email: '',
          password: '',
          passwordConfirmation: '',
          captchaId: '',
          captcha: ''
        },
        CAPTCHA_METHODS
      }
    },
    computed: {
      ...mapState(CAPTCHA.NAMESPACE, {
        captchaMethod: state => (state[CAPTCHA.STATE.METHOD] || ''),
        captchaId: state => (state[CAPTCHA.STATE.CAPTCHA_ID] || '')
      }),
      flag () {
        return this.$t('Даю свое согласие на обработку моих персональных данных, в соответствии с Федеральным законом от 27.07.2006 года №152-ФЗ «О персональных данных»')
      }
    },
    created () {
      this.goToDefaultRoute()
      this.getCaptchaMethod()
    },
    methods: {
      ...mapActions(ACCOUNT.NAMESPACE, {
        registerUserAction: ACCOUNT.ACTIONS.REGISTER_USER
      }),
      ...mapActions(CAPTCHA.NAMESPACE, {
        getCaptchaMethodAction: CAPTCHA.ACTIONS.GET_CAPTCHA_METHOD
      }),
      validateAndRegister: async function () {
        await this.hideErrorAction()
        const result = await this.$refs.registrationform.validate()
        if (result) {
          this.sendRegistrationRequest(this.newUser)
        }
      },
      sendRegistrationRequest: async function (model) {
        const vm = this
        vm.isLoading = true
        try {
          await vm.registerUserAction(model)
          vm.showConfirmation = true
          setTimeout(function () {
            vm.goToRoute(`${vm.urlRootPath}/login`)
          }, 6000)
        } catch (error) {
          // сброс каптчи, если с сервера пришла ошибка
          if (vm.captchaMethod === CAPTCHA_METHODS.GOOGLE) {
            vm.$refs.recaptcha.resetCaptcha()
          }
          vm.throwIfNotServerError(error)
        } finally {
          vm.isLoading = false
        }
      },
      goToDefaultRoute: function () {
        if (!this.isLoggedIn) {
          return
        }
        // если пользователь аутентифицирован, то перенаправляем его
        if (!this.isRegistered) {
          this.goToRoute(`${this.urlRootPath}/user/registration/start`)
        } else {
          this.goToRoute(`${this.urlRootPath}/`)
        }
      },
      cancel: function () {
        // console.log('Пользователь отказался от регистрации.')
        this.goToRoute(`${this.urlRootPath}/login`)
      },
      getCaptchaMethod: async function () {
        this.isLoading = true
        try {
          await this.getCaptchaMethodAction()
        } catch (error) {
          this.throwIfNotServerError(error)
        } finally {
          this.isLoading = false
        }
      },
      inputCaptchaGen: function (value) {
        this.newUser.captcha = value
        this.newUser.captchaId = this.captchaId
      }
    }
  }
</script>
